/**
 * The window a case list opens on.
 *
 * The existing design has always opened on the last 30 days, which is where
 * the cases whose photographs still exist live. The redesign opened on a fixed
 * start date in June 2025 instead, so its first page was the oldest cases the
 * backend has — whose media has since been purged — and every Bajaj row showed
 * a placeholder under a row that said "Newest first". Same window for both
 * designs, declared once.
 *
 * Pure, so it can be tested with an injected clock.
 */
export const DEFAULT_LIST_DAYS = 30;

const toIsoDate = (date) => date.toISOString().split("T")[0];

/** `{ startDate, endDate }` as the API wants them: YYYY-MM-DD, inclusive. */
export const defaultListRange = (now = new Date(), days = DEFAULT_LIST_DAYS) => {
  const end = new Date(now);
  const start = new Date(now);
  start.setDate(start.getDate() - days);
  return { startDate: toIsoDate(start), endDate: toIsoDate(end) };
};

/**
 * Parses `created_at` the way this API family actually sends it.
 *
 * "2026-09-11 02:19:49" is what the API sends. Chrome parses the space, Safari
 * does not — it returns Invalid Date, and a NaN comparator makes the sort
 * order implementation-defined. The T form is parsed everywhere. Returns
 * null, not NaN, for anything that doesn't parse, so a caller can tell
 * "unparseable" apart from "midnight, 1970" and choose to keep a row rather
 * than silently drop it over a quirk in the field.
 */
export const parseCreatedAt = (value) => {
  const text = String(value ?? "").trim().replace(" ", "T");
  if (!text) return null;
  const time = new Date(text).getTime();
  return Number.isFinite(time) ? time : null;
};

/**
 * Newest first, by created_at, which is the order the existing design shows
 * a page in. The API returns a dated window oldest-first, so without this the
 * first row is the oldest case in the window.
 */
const receivedAt = (row) => parseCreatedAt(row?.created_at) ?? 0;

export const newestFirst = (rows = []) =>
  [...rows].sort((a, b) => receivedAt(b) - receivedAt(a));

/**
 * True when `createdAt` falls inside `{ startDate, endDate }` (YYYY-MM-DD,
 * inclusive of the whole end day) — or when there's no window, or the value
 * can't be parsed, since this guard exists to narrow a response that wasn't
 * scoped server-side, not to silently drop a row nothing proves is outside it.
 *
 * Needed for Hegic (v2): getAssessmentListV2 (apiService.js) takes no
 * start_date/stop_date at all, so picking a range in the calendar changed the
 * chip's own label but never touched which rows came back — the same claims
 * showed no matter what was picked, which is what "the calendar filter is out
 * of sync" actually was. This is purely about the calendar doing what it
 * says; it is not a stand-in for a trustworthy total (see
 * CasesRedesign.js's fetchAssessmentReport for why that's asked for
 * unscoped instead).
 */
export const withinListRange = (createdAt, { startDate, endDate } = {}) => {
  if (!startDate || !endDate) return true;
  const time = parseCreatedAt(createdAt);
  if (time === null) return true;
  const start = new Date(`${startDate}T00:00:00`).getTime();
  const end = new Date(`${endDate}T23:59:59.999`).getTime();
  return time >= start && time <= end;
};

export default defaultListRange;
